import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import { forwardRef, inject, Injectable } from '@angular/core';
import { ChecklistTemplateEntry } from '../../models/checklist-template-entry/checklist-template-entry.model';
import { Observable } from 'rxjs/internal/Observable';
import { ChecklistTemplateEntryManagerAction } from './checklist-template-entry-manager.action';
import { concatMap, EMPTY, finalize, from, of, switchMap, throwError } from 'rxjs';
import { CompoApp } from '../../services/CompoApp';
import { ChecklistTemplateDetailXsState } from '../checklist-template/checklist-template-detail-xs.state';
import { catchError, filter, map, tap } from 'rxjs/operators';
import { ChecklistTemplateDetailAction } from '../checklist-template/checklist-template-detail.action';
import { ChecklistEntryType } from '../../enums/checklist-entry/checklist-entry-type.enum';
import {
  CreateChecklistTemplateEntryDto
} from '../../models/checklist-template-entry/create-checklist-template-entry.model';
import {
  BoundingChecklistTemplateEntry
} from '../../models/checklist-template-entry/bounding-checklist-template-entries.model';
import { Languages } from '../../../assets/i18n/Languages';
import {
  ChecklistTemplateEntryNumbering
} from '../../models/checklist-template/checklist-template-entry-numbering.model';
import {
  ChecklistTemplateEntryManagerMode
} from '../../enums/checklist-template/checklist-template-entry-manager-mode.enum';
import { ResetStateDefaults } from '../base-action-class.model';
import {
  ChecklistTemplateEntrySeverityActionCommon
} from '../../models/checklist-template-entry/checklist-template-entry-severity-action.common';
import { ChecklistTemplateEntryRequestService } from '../../services/request/checklist-template-entry-request.service';
import { FileUploadError, FileUploaderUtils } from '../../content/components/file-uploader/FileUploaderUtils';
import { MessageDesignBUtils } from '../../content/components/message-design-b/message-design-b.utils';
import { DocumentModel } from '../../models/document/document.model';
import {
  ChecklistTemplateEntryOption
} from '../../models/checklist-template-entry/checklist-template-entry-option.model';
import { ProcessUtils } from '../../utils/ProcessUtils';
import { DocumentGroupReferenceType } from '../../enums/document/DocumentGroupReferenceType';
import { DocumentGroupModel } from '../../models/document/document-group.model';

interface ChecklistTemplateEntryManagerStateModel {
  active: boolean;
  mode: ChecklistTemplateEntryManagerMode;
  id: string;
  checklistTemplateEntry: ChecklistTemplateEntry;
  updatedChecklistTemplateEntry: ChecklistTemplateEntry;
  insertAfterEntryId: string;
  insertBeforeEntryId: string;
  groupedWithNext: boolean;
  numberingValue: string;
  inProgress: boolean;
  entryImage: File;
  optionImages: Map<number, File>;
  optionFiles: boolean;
  filesToDelete: boolean;
  removeImages: { entry: boolean, options: Map<number, boolean> };
}

const getDefaults = (): ChecklistTemplateEntryManagerStateModel => ({
  active: false,
  mode: null,
  id: null,
  checklistTemplateEntry: null,
  updatedChecklistTemplateEntry: null,
  insertAfterEntryId: null,
  insertBeforeEntryId: null,
  groupedWithNext: null,
  numberingValue: null,
  inProgress: false,
  entryImage: null,
  optionImages: new Map<number, File>,
  optionFiles: false,
  filesToDelete: false,
  removeImages: {entry: false, options: new Map<number, boolean>},
});

@State<ChecklistTemplateEntryManagerStateModel>({
  name: 'checklistTemplateEntryManager',
  defaults: getDefaults()
})
@Injectable()
export class ChecklistTemplateEntryManagerXsState {

  //region INJECTIONS
  private app = inject(CompoApp);
  private store = inject(Store);
  private checklistTemplateEntryRequestService = inject(ChecklistTemplateEntryRequestService);
  //endregion

  //region SELECTORS
  @Selector()
  public static hasUnsavedChanges(state: ChecklistTemplateEntryManagerStateModel): boolean {
    if (state.mode == ChecklistTemplateEntryManagerMode.CREATE) {
      return state.active;
    } else {
      return this.saveRequired(state);
    }
  }

  @Selector()
  public static active(state: ChecklistTemplateEntryManagerStateModel): boolean {
    return state.active;
  }

  @Selector()
  public static inProgress(state: ChecklistTemplateEntryManagerStateModel): boolean {
    return state.inProgress;
  }

  @Selector()
  public static mode(state: ChecklistTemplateEntryManagerStateModel): ChecklistTemplateEntryManagerMode | null {
    return state.mode;
  }

  @Selector()
  public static checklistTemplateEntryId(state: ChecklistTemplateEntryManagerStateModel): string | null {
    return state.id;
  }

  @Selector()
  public static checklistTemplateEntry(state: ChecklistTemplateEntryManagerStateModel): ChecklistTemplateEntry | null {
    return state.checklistTemplateEntry;
  }

  @Selector()
  public static updatedChecklistTemplateEntry(
    state: ChecklistTemplateEntryManagerStateModel
  ): ChecklistTemplateEntry | null {
    return state.updatedChecklistTemplateEntry;
  }

  @Selector()
  public static entryImage(state: ChecklistTemplateEntryManagerStateModel): File | null {
    return state.entryImage;
  }

  @Selector()
  public static optionImages(state: ChecklistTemplateEntryManagerStateModel): Map<number, File> | null {
    return state.optionImages;
  }

  @Selector()
  public static removeImages(state: ChecklistTemplateEntryManagerStateModel): any | null {
    return state.removeImages;
  }

  @Selector()
  public static saveRequired(
    state: ChecklistTemplateEntryManagerStateModel): boolean {
    if (!state.active) {
      return false;
    }

    if (state.mode === ChecklistTemplateEntryManagerMode.CREATE) {
      return true;
    }

    if (!state.id || !state.updatedChecklistTemplateEntry || !state.checklistTemplateEntry) {
      return false;
    }

    for (const [key, updatedValue] of Object.entries(state.updatedChecklistTemplateEntry)) {
      if ((typeof updatedValue !== 'object' || updatedValue == null)
        && state.checklistTemplateEntry[key] != updatedValue) {
        return true;
      }
    }

    if ((state.updatedChecklistTemplateEntry.name?.translations[Languages.EN] || '')
      != (state.checklistTemplateEntry.name?.translations[Languages.EN] || '')
      || (state.updatedChecklistTemplateEntry.name?.translations[Languages.DE] || '')
      != (state.checklistTemplateEntry.name?.translations[Languages.DE] || '')) {
      return true;
    }

    if ((state.updatedChecklistTemplateEntry.description?.translations[Languages.EN] || '')
      != (state.checklistTemplateEntry.description?.translations[Languages.EN] || '')
      || (state.updatedChecklistTemplateEntry.description?.translations[Languages.DE] || '')
      != (state.checklistTemplateEntry.description?.translations[Languages.DE] || '')) {
      return true;
    }

    if (state.updatedChecklistTemplateEntry.options?.length != state.checklistTemplateEntry.options?.length) {
      return true;
    }

    if (state.updatedChecklistTemplateEntry.options && state.checklistTemplateEntry.options) {
      for (let i = 0; i < state.updatedChecklistTemplateEntry.options.length; i++) {
        if ((state.updatedChecklistTemplateEntry.options[i]?.nameTranslation?.translations[Languages.EN] || '')
          != (state.checklistTemplateEntry.options[i]?.nameTranslation?.translations[Languages.EN] || '')
          || (state.updatedChecklistTemplateEntry.options[i]?.nameTranslation?.translations[Languages.DE] || '')
          != (state.checklistTemplateEntry.options[i]?.nameTranslation?.translations[Languages.DE] || '')) {
          return true;
        }

        if ((state.updatedChecklistTemplateEntry.options[i]?.imageRemark?.translations[Languages.EN] || '')
          != (state.checklistTemplateEntry.options[i]?.imageRemark?.translations[Languages.EN] || '')
          || (state.updatedChecklistTemplateEntry.options[i]?.imageRemark?.translations[Languages.DE] || '')
          != (state.checklistTemplateEntry.options[i]?.imageRemark?.translations[Languages.DE] || '')) {
          return true;
        }

        if ((state.updatedChecklistTemplateEntry.options[i]?.infoTextTranslation?.translations[Languages.EN] || '')
          != (state.checklistTemplateEntry.options[i]?.infoTextTranslation?.translations[Languages.EN] || '')
          || (state.updatedChecklistTemplateEntry.options[i]?.infoTextTranslation?.translations[Languages.DE] || '')
          != (state.checklistTemplateEntry.options[i]?.infoTextTranslation?.translations[Languages.DE] || '')) {
          return true;
        }

        if (state.updatedChecklistTemplateEntry.options[i]?.imageWidth
          != state.checklistTemplateEntry.options[i]?.imageWidth) {
          return true;
        }

        if ((state.updatedChecklistTemplateEntry.options[i]?.severityActions || []).length
          != (state.checklistTemplateEntry.options[i]?.severityActions || []).length) {
          return true;
        }

        if ((state.updatedChecklistTemplateEntry.options[i]?.attributeEnumOptionId)
          != (state.checklistTemplateEntry.options[i]?.attributeEnumOptionId)) {
          return true;
        }

        if (!!state.updatedChecklistTemplateEntry.options[i]?.severityActions
          && !!state.checklistTemplateEntry.options[i]?.severityActions) {
          for (let j = 0; j < state.updatedChecklistTemplateEntry.options[i]?.severityActions?.length; j++) {
            const updatedSeverityAction: ChecklistTemplateEntrySeverityActionCommon
              = state.updatedChecklistTemplateEntry.options[i].severityActions[j];
            const severityAction: ChecklistTemplateEntrySeverityActionCommon
              = state.checklistTemplateEntry.options[i].severityActions[j];
            for (const [key, updatedValue] of Object.entries(updatedSeverityAction)) {
              if (severityAction[key] != updatedValue) {
                return true;
              }
            }
          }
        }
      }
    }

    if (state.updatedChecklistTemplateEntry.ranges?.length != state.checklistTemplateEntry.ranges?.length) {
      return true;
    }

    if (state.updatedChecklistTemplateEntry.ranges && state.checklistTemplateEntry.ranges) {
      for (let i = 0; i < state.updatedChecklistTemplateEntry.ranges.length; i++) {

        for (const [key, updatedValue] of Object.entries(state.updatedChecklistTemplateEntry.ranges[i])) {
          if ((typeof updatedValue !== 'object' || updatedValue == null)
            && state.checklistTemplateEntry.ranges[i][key] != updatedValue) {
            return true;
          }
        }

        if ((state.updatedChecklistTemplateEntry.ranges[i]?.severityActions || []).length
          != (state.checklistTemplateEntry.ranges[i]?.severityActions || []).length) {
          return true;
        }

        if ((state.updatedChecklistTemplateEntry.ranges[i]?.overallAssessment)
          != (state.checklistTemplateEntry.ranges[i]?.overallAssessment)) {
          return true;
        }


        if (!!state.updatedChecklistTemplateEntry.ranges[i]?.severityActions
          && !!state.checklistTemplateEntry.ranges[i]?.severityActions) {
          for (let j = 0; j < state.updatedChecklistTemplateEntry.ranges[i]?.severityActions?.length; j++) {
            const updatedSeverityAction: ChecklistTemplateEntrySeverityActionCommon
              = state.updatedChecklistTemplateEntry.ranges[i].severityActions[j];
            const severityAction: ChecklistTemplateEntrySeverityActionCommon
              = state.checklistTemplateEntry.ranges[i].severityActions[j];
            for (const [key, updatedValue] of Object.entries(updatedSeverityAction)) {
              if (severityAction[key] != updatedValue) {
                return true;
              }
            }
          }
        }
      }
    }

    if ((state.updatedChecklistTemplateEntry.rowTitle?.translations[Languages.EN] || '')
      != (state.checklistTemplateEntry.rowTitle?.translations[Languages.EN] || '')
      || (state.updatedChecklistTemplateEntry.rowTitle?.translations[Languages.DE] || '')
      != (state.checklistTemplateEntry.rowTitle?.translations[Languages.DE] || '')) {
      return true;
    }

    if ((state.updatedChecklistTemplateEntry.columnTitle?.translations[Languages.EN] || '')
      != (state.checklistTemplateEntry.columnTitle?.translations[Languages.EN] || '')
      || (state.updatedChecklistTemplateEntry.columnTitle?.translations[Languages.DE] || '')
      != (state.checklistTemplateEntry.columnTitle?.translations[Languages.DE] || '')) {
      return true;
    }

    if (state.updatedChecklistTemplateEntry.constraint && state.checklistTemplateEntry.constraint) {
      for (const [key, updatedValue] of Object.entries(state.updatedChecklistTemplateEntry.constraint)) {
        if (typeof updatedValue !== 'object' || updatedValue == null) {
          if (typeof updatedValue === 'boolean') {
            if (!!state.checklistTemplateEntry.constraint[key] != !!updatedValue) {
              return true;
            }
          } else {
            if (state.checklistTemplateEntry.constraint[key] != updatedValue) {
              return true;
            }
          }
        }
      }
    }

    if ((state.updatedChecklistTemplateEntry.imageRemark?.translations[Languages.EN] || '')
      != (state.checklistTemplateEntry.imageRemark?.translations[Languages.EN] || '')
      || (state.updatedChecklistTemplateEntry.imageRemark?.translations[Languages.DE] || '')
      != (state.checklistTemplateEntry.imageRemark?.translations[Languages.DE] || '')) {
      return true;
    }


    if (state.updatedChecklistTemplateEntry.imageWidth !== state.checklistTemplateEntry.imageWidth) {
      return true;
    }

    if (state.entryImage != null || state.optionImages.size > 0) {
      return true;
    }

    if (state.optionFiles) {
      return true;
    }

    if (state.filesToDelete) {
      return true;
    }

    if (state.removeImages.entry || state.removeImages.options.size > 0) {
      return true;
    }

    return false;
  }

  @Selector()
  public static numberingValue(state: ChecklistTemplateEntryManagerStateModel): string | null {
    return state.numberingValue;
  }

  @Selector()
  public static insertAfterEntryId(state: ChecklistTemplateEntryManagerStateModel): string | null {
    return state.insertAfterEntryId;
  }

  @Selector()
  public static insertBeforeEntryId(state: ChecklistTemplateEntryManagerStateModel): string | null {
    return state.insertBeforeEntryId;
  }

  @Selector()
  public static groupedWithPrevious(state: ChecklistTemplateEntryManagerStateModel): boolean | null {
    return state.updatedChecklistTemplateEntry
      ? state.updatedChecklistTemplateEntry.groupedWithPrevious
      : state.checklistTemplateEntry?.groupedWithPrevious;
  }

  @Selector()
  public static groupedWithNext(state: ChecklistTemplateEntryManagerStateModel): boolean | null {
    return state.groupedWithNext;
  }

  @Selector()
  public static showNumbering(state: ChecklistTemplateEntryManagerStateModel): boolean | null {
    return state.updatedChecklistTemplateEntry
      ? state.updatedChecklistTemplateEntry.showNumbering
      : state.checklistTemplateEntry?.showNumbering;
  }

  @Selector()
  public static numbering(state: ChecklistTemplateEntryManagerStateModel): string {
    return state.numberingValue;
  }

  @Selector()
  public static shiftLeftAllowed(state: ChecklistTemplateEntryManagerStateModel): boolean | null {
    if (state.mode !== ChecklistTemplateEntryManagerMode.EDIT) {
      return false;
    }

    return state.checklistTemplateEntry.numberingLevel > 1;
  }

  @Selector([
    (forwardRef(() => ChecklistTemplateDetailXsState) as any).checklistTemplateEntryIdsInOrder,
    (forwardRef(() => ChecklistTemplateDetailXsState) as any).checklistTemplateEntries
  ])
  public static shiftRightAllowedOld(
    state: ChecklistTemplateEntryManagerStateModel,
    checklistTemplateEntryIdsInOrder: string[],
    checklistTemplateEntries: Map<string, ChecklistTemplateEntry>
  ): boolean | null {
    if (state.mode !== ChecklistTemplateEntryManagerMode.EDIT) {
      return false;
    }

    const index: number = checklistTemplateEntryIdsInOrder?.findIndex((id: string) => id === state.id);

    if (index === null || index === 0 || index === -1) {
      return false;
    }

    const previousEntry: ChecklistTemplateEntry
      = checklistTemplateEntries.get(checklistTemplateEntryIdsInOrder[index - 1]);

    if (previousEntry.type === ChecklistEntryType.TABLE_ROW) {
      return previousEntry.numberingLevel - 1 <= state.checklistTemplateEntry.numberingLevel;
    } else {
      return previousEntry.numberingLevel + 1 < state.checklistTemplateEntry.numberingLevel;
    }
  }

  public static shiftRightAllowed() {
    return createSelector(
      [
        ChecklistTemplateEntryManagerXsState,
        ChecklistTemplateDetailXsState.checklistTemplateEntryIdsInOrder,
        ChecklistTemplateDetailXsState.checklistTemplateEntries
      ],
      (
        state: ChecklistTemplateEntryManagerStateModel,
        checklistTemplateEntryIdsInOrder: string[],
        checklistTemplateEntries: Map<string, ChecklistTemplateEntry>
      ): boolean => {
        if (state.mode !== ChecklistTemplateEntryManagerMode.EDIT) {
          return false;
        }

        const index: number = checklistTemplateEntryIdsInOrder?.findIndex((id: string) => id === state.id);

        if (index === null || index === 0 || index === -1) {
          return false;
        }

        const previousEntry: ChecklistTemplateEntry
          = checklistTemplateEntries.get(checklistTemplateEntryIdsInOrder[index - 1]);

        if (previousEntry.type === ChecklistEntryType.TABLE_ROW) {
          return !(previousEntry.numberingLevel - 1 <= state.checklistTemplateEntry.numberingLevel);
        } else {
          return !(previousEntry.numberingLevel + 1 <= state.checklistTemplateEntry.numberingLevel);
        }
      });
  }

  @Selector()
  public static isDecimalConstraint(state: ChecklistTemplateEntryManagerStateModel): boolean {
    return !!state.updatedChecklistTemplateEntry?.constraint?.isDecimal;
  }

  //endregion

  //region ACTIONS
  @Action([ChecklistTemplateEntryManagerAction.Create])
  public create(
    ctx: StateContext<ChecklistTemplateEntryManagerStateModel>,
    action: ChecklistTemplateEntryManagerAction.Create
  ): Observable<void> {
    const state: ChecklistTemplateEntryManagerStateModel = ctx.getState();

    if (action.insertAfterEntryId != null && action.insertBeforeEntryId != null) {
      return throwError(() =>
        new Error('Exactly one of the insert after/insert before entry ids must be set')
      );
    }

    if (action.type === ChecklistEntryType.TABLE_ROW && !action.tableType) {
      return throwError(() =>
        new Error('Table type cannot be null if type is "TABLE_ROW"!')
      );
    }

    if (state.active && this.store.selectSnapshot(ChecklistTemplateEntryManagerXsState.saveRequired)) {
      return throwError(() =>
        new Error('Cannot create checklist template entry! Manager is already active and save is required!')
      );
    }

    if (state.inProgress) {
      return throwError(() => new Error('Checklist template entry request is in progress!'));
    }

    let groupedWithPrevious: boolean = false;
    let groupedWithNext: boolean = false;

    if (!action.type || action.type !== ChecklistEntryType.PAGE_BREAK) {
      if (action.insertAfterEntryId) {
        const nextEntry: BoundingChecklistTemplateEntry = this.store.selectSnapshot(
          ChecklistTemplateDetailXsState.boundingChecklistTemplateEntries(action.insertAfterEntryId)
        )?.next;
        groupedWithPrevious
          = groupedWithNext
          = !!nextEntry?.groupedWithPrevious || action.type === ChecklistEntryType.TABLE_ROW;
        groupedWithNext = !!nextEntry?.groupedWithPrevious;
      } else if (action.insertBeforeEntryId) {
        const previousEntry: BoundingChecklistTemplateEntry = this.store.selectSnapshot(
          ChecklistTemplateDetailXsState.checklistTemplateEntry(action.insertBeforeEntryId)
        );
        groupedWithPrevious
          = groupedWithNext
          = !!previousEntry?.groupedWithPrevious || action.type === ChecklistEntryType.TABLE_ROW;
        groupedWithNext = !!previousEntry?.groupedWithPrevious || action.type === ChecklistEntryType.TABLE_ROW;
      }
    }

    ctx.setState({
      ...getDefaults(),
      inProgress: true
    });

    const checklistTemplateId: string = this.store.selectSnapshot(ChecklistTemplateDetailXsState.id);

    return from(this.checklistTemplateEntryRequestService.getNumberingForEntry(
      checklistTemplateId,
      action.type,
      action.insertAfterEntryId,
      action.insertBeforeEntryId,
      null
    )).pipe(
      map((numbering: ChecklistTemplateEntryNumbering) => {
        ctx.patchState({
          active: true,
          mode: ChecklistTemplateEntryManagerMode.CREATE,
          insertBeforeEntryId: action.insertBeforeEntryId,
          insertAfterEntryId: action.insertAfterEntryId,
          groupedWithNext,
          checklistTemplateEntry: {
            id: null,
            groupedWithPrevious,
            type: action.type,
            tableType: action.tableType,
            showNumbering: numbering.showNumbering,
            isMainTitle: true
          } as ChecklistTemplateEntry,
          numberingValue: numbering.numberingValue,
          inProgress: false
        });
      }),
      finalize(() => ctx.patchState({ inProgress: false }))
    );
  }

  @Action(ChecklistTemplateEntryManagerAction.CreatePageBreak)
  public createPageBreak(
    ctx: StateContext<ChecklistTemplateEntryManagerStateModel>,
    action: ChecklistTemplateEntryManagerAction.CreatePageBreak
  ): Observable<void> {
    const state: ChecklistTemplateEntryManagerStateModel = ctx.getState();

    if ((action.insertAfterEntryId != null && action.insertBeforeEntryId != null)
      || (action.insertAfterEntryId == null && action.insertBeforeEntryId == null)) {
      return throwError(() =>
        new Error('Exactly one of the insert after/insert before entry ids must be set')
      );
    }

    if (state.inProgress) {
      return throwError(() => new Error('Checklist template entry request is in progress!'));
    }

    const checklistTemplateId = this.store.selectSnapshot(ChecklistTemplateDetailXsState.id);

    if (checklistTemplateId == null) {
      return throwError(() =>
        new Error('Cannot save checklist template entry! State returned null id for checklist template!')
      );
    }

    const createChecklistTemplateEntryDto: CreateChecklistTemplateEntryDto = {
      type: ChecklistEntryType.PAGE_BREAK,
      insertAfterEntryId: action.insertAfterEntryId,
      insertBeforeEntryId: action.insertBeforeEntryId
    } as CreateChecklistTemplateEntryDto;

    ctx.patchState({
      inProgress: true
    });

    return from(this.checklistTemplateEntryRequestService.createChecklistTemplateEntry(
      checklistTemplateId,
      createChecklistTemplateEntryDto,
      null,
      null
    ))
      .pipe(
        switchMap((checklistTemplateEntry: ChecklistTemplateEntry) => {
          return this.store.dispatch(
            new ChecklistTemplateDetailAction.ChecklistTemplateEntryCreated(
              checklistTemplateEntry.id,
              checklistTemplateEntry
            )
          );
        }),
        tap(() => ctx.setState(getDefaults)),
        finalize(() => ctx.patchState({ inProgress: false }))
      );
  }

  @Action(ChecklistTemplateEntryManagerAction.Edit)
  public edit(
    ctx: StateContext<ChecklistTemplateEntryManagerStateModel>,
    action: ChecklistTemplateEntryManagerAction.Edit
  ): Observable<void> {
    const state: ChecklistTemplateEntryManagerStateModel = ctx.getState();

    if (action.id.length == 0) {
      throw new Error('Id cannot be null!');
    }

    if (state.active) {
      throw new Error('Cannot edit checklist template entry! Manager is already active!');
    }

    if (state.inProgress) {
      throw new Error('Checklist template entry request is in progress!');
    }

    const checklistTemplateId = this.store.selectSnapshot(ChecklistTemplateDetailXsState.id);

    if (checklistTemplateId == null) {
      return throwError(() =>
        new Error('Cannot save checklist template entry! State returned null id for checklist template!')
      );
    }

    const checklistTemplateEntry: ChecklistTemplateEntry
      = this.store.selectSnapshot(ChecklistTemplateDetailXsState.checklistTemplateEntry(action.id));

    if (!checklistTemplateEntry) {
      throw new Error('Cannot find checklist template entry!');
    }

    if (checklistTemplateEntry.showNumbering && checklistTemplateEntry.numberingValue) {

      ctx.setState({
        ...getDefaults(),
        active: true,
        mode: ChecklistTemplateEntryManagerMode.EDIT,
        id: action.id,
        checklistTemplateEntry,
        numberingValue: checklistTemplateEntry.numberingValue
      });

      // Observable expected as return value
      return of();
    } else {

      ctx.setState({
        ...getDefaults(),
        inProgress: true
      });

      return from(this.checklistTemplateEntryRequestService.getNumberingForEntry(
        checklistTemplateId,
        checklistTemplateEntry.type,
        null,
        null,
        checklistTemplateEntry.column
      )).pipe(
        map((numbering: ChecklistTemplateEntryNumbering) => {
          ctx.patchState({
            active: true,
            mode: ChecklistTemplateEntryManagerMode.EDIT,
            id: action.id,
            checklistTemplateEntry,
            numberingValue: numbering.numberingValue,
            inProgress: false
          });
        }),
        finalize(() => ctx.patchState({ inProgress: false }))
      );
    }
  }

  @Action(ChecklistTemplateEntryManagerAction.UpdateEntry)
  public updateEntryState(
    ctx: StateContext<ChecklistTemplateEntryManagerStateModel>,
    action: ChecklistTemplateEntryManagerAction.UpdateEntry
  ): void {
    const state: ChecklistTemplateEntryManagerStateModel = ctx.getState();

    if (state.inProgress) {
      throw new Error('Checklist template entry request is in progress!');
    }

    const updatedChecklistTemplateEntry: ChecklistTemplateEntry = {
      ...state.checklistTemplateEntry,
      ...state.updatedChecklistTemplateEntry,
      ...action.checklistTemplateEntry
    };

    ctx.patchState({
      updatedChecklistTemplateEntry
    });
  }

  @Action(ChecklistTemplateEntryManagerAction.Save)
  public save(
    ctx: StateContext<ChecklistTemplateEntryManagerStateModel>
  ): Observable<void> {
    const state: ChecklistTemplateEntryManagerStateModel = ctx.getState();

    if (!state.active || !state.mode) {
      return throwError(() =>
        new Error('Cannot save checklist template entry when state is not active or edit mode is not defined!')
      );
    }

    if (!state.checklistTemplateEntry) {
      return throwError(() => new Error(
        'Checklist template entry cannot be null! Entry state must be updated before saving the entry!'
      ));
    }

    if (state.inProgress) {
      return throwError(() => new Error('Checklist template entry request is in progress!'));
    }

    const checklistTemplateId = this.store.selectSnapshot(ChecklistTemplateDetailXsState.id);

    if (checklistTemplateId == null) {
      return throwError(() =>
        new Error('Cannot save checklist template entry! State returned null id for checklist template!')
      );
    }

    ctx.patchState({
      inProgress: true
    });

    switch (state.mode) {
      case ChecklistTemplateEntryManagerMode.CREATE: {

        const createChecklistTemplateEntryDto: CreateChecklistTemplateEntryDto = {
          ...state.updatedChecklistTemplateEntry,
          insertAfterEntryId: state.insertAfterEntryId,
          insertBeforeEntryId: state.insertBeforeEntryId,
          groupedWithNext: state.groupedWithNext
        };

        const entryImage = state.entryImage;
        const optionImages = state.optionImages;
        const optionFiles = state.optionFiles;

        return from(this.checklistTemplateEntryRequestService.createChecklistTemplateEntry(
          checklistTemplateId,
          createChecklistTemplateEntryDto,
          entryImage,
          optionImages
        ))
          .pipe(
            switchMap((checklistTemplateEntry: ChecklistTemplateEntry) => {
              return this.store.dispatch(new ChecklistTemplateDetailAction.ChecklistTemplateEntryCreated(
                  checklistTemplateEntry.id,
                  checklistTemplateEntry
                )
              );
            }),
            tap(() => ctx.setState(getDefaults)),
            finalize(() => ctx.patchState({ inProgress: false }))
          );
      }
      case ChecklistTemplateEntryManagerMode.EDIT: {

        const checklistTemplateEntry: ChecklistTemplateEntry = { ...state.updatedChecklistTemplateEntry, id: state.id };

        const entryImage = state.entryImage;
        const optionImages = state.optionImages;
        const removeImages = state.removeImages;

        return from(this.checklistTemplateEntryRequestService.updateChecklistTemplateEntry(
          checklistTemplateId,
          checklistTemplateEntry,
          entryImage,
          optionImages
        ))
          .pipe(
            switchMap((checklistTemplateEntry: ChecklistTemplateEntry) => {
              const deleteImageActions: ChecklistTemplateEntryManagerAction.DeleteImage[] = [];
              if (removeImages.entry) {
                deleteImageActions.push(new ChecklistTemplateEntryManagerAction.DeleteImage(null));
                checklistTemplateEntry.image = null;
              }
              removeImages.options.forEach((value, optionIndex) => {
                if (value) {
                  deleteImageActions.push(new ChecklistTemplateEntryManagerAction.DeleteImage(optionIndex));
                  checklistTemplateEntry.options[optionIndex].image = null;
                }
              });
              if (deleteImageActions.length == 0) {
                return of(checklistTemplateEntry);
              } else {
                return this.store.dispatch(deleteImageActions).pipe(
                  map(() => checklistTemplateEntry));
              }
            }),
            switchMap((checklistTemplateEntry: ChecklistTemplateEntry) => {
              return this.store.dispatch(new ChecklistTemplateDetailAction.ChecklistTemplateEntryUpdated(
                state.id,
                checklistTemplateEntry
              ));
            }),
            tap(() => ctx.setState(getDefaults)),
            finalize(() => ctx.patchState({ inProgress: false }))
          );
      }
    }
  }

  @Action(ChecklistTemplateEntryManagerAction.Delete)
  public delete(
    ctx: StateContext<ChecklistTemplateEntryManagerStateModel>,
    action: ChecklistTemplateEntryManagerAction.Delete
  ): Observable<void> {
    const state: ChecklistTemplateEntryManagerStateModel = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Checklist template entry request is in progress!'));
    }

    if (!state.active && !action.id) {
      return throwError(() =>
        new Error('Checklist template entry id cannot be null when manager is not active!')
      );
    }

    const checklistTemplateId = this.store.selectSnapshot(ChecklistTemplateDetailXsState.id);

    if (checklistTemplateId == null) {
      return throwError(() =>
        new Error('Cannot delete checklist template entry! State returned null id for checklist template!')
      );
    }

    if (!state.active || (state.mode === ChecklistTemplateEntryManagerMode.EDIT && state.id)) {
      let checklistTemplateEntryIdToDelete: string;
      if (state.active) {
        checklistTemplateEntryIdToDelete = state.id;
      } else {
        checklistTemplateEntryIdToDelete = action.id;
      }

      ctx.patchState({
        inProgress: true
      });

      return from(this.checklistTemplateEntryRequestService.deleteChecklistTemplateEntry(
        checklistTemplateId,
        checklistTemplateEntryIdToDelete,
        action.wholeBlock
      ))
        .pipe(
          switchMap(() => {
            return this.store.dispatch(new ChecklistTemplateDetailAction.ChecklistTemplateEntryDeleted(
              checklistTemplateEntryIdToDelete
            ));
          }),
          tap(() => ctx.setState(getDefaults)),
          finalize(() => ctx.patchState({ inProgress: false }))
        );
    } else {
      ctx.setState(getDefaults());
      return of(null);
    }
  }

  @Action([ChecklistTemplateEntryManagerAction.Group, ChecklistTemplateEntryManagerAction.Ungroup])
  public groupAndUngroup(
    ctx: StateContext<ChecklistTemplateEntryManagerStateModel>,
    action: ChecklistTemplateEntryManagerAction.Group | ChecklistTemplateEntryManagerAction.Ungroup
  ): Observable<void> {
    const state: ChecklistTemplateEntryManagerStateModel = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Checklist template entry request is in progress!'));
    }

    if (!state.active && !action.id) {
      return throwError(() => new Error('Checklist template id cannot be null if manager is not active!'));
    }

    const checklistTemplateId = this.store.selectSnapshot(ChecklistTemplateDetailXsState.id);

    if (checklistTemplateId == null) {
      return throwError(() =>
        new Error('Cannot save checklist template entry! State returned null id for checklist template!')
      );
    }

    if (state.active && state.mode === ChecklistTemplateEntryManagerMode.CREATE) {

      return throwError(() => new Error('Not implemented!'));

    } else {

      ctx.patchState({
        inProgress: true
      });

      if (action instanceof ChecklistTemplateEntryManagerAction.Group) {
        return from(this.checklistTemplateEntryRequestService.groupWithPrevious(
          checklistTemplateId,
          action.id
        ))
          .pipe(
            switchMap(() => {
              return this.store.dispatch(new ChecklistTemplateDetailAction.ChecklistTemplateEntryGrouped(
                action.id
              ));
            }),
            finalize(() => ctx.patchState({ inProgress: false }))
          );
      }

      if (action instanceof ChecklistTemplateEntryManagerAction.Ungroup) {
        return from(this.checklistTemplateEntryRequestService.ungroupWithPrevious(
          checklistTemplateId,
          action.id
        ))
          .pipe(
            switchMap(() => {
              return this.store.dispatch(new ChecklistTemplateDetailAction.ChecklistTemplateEntryUngrouped(
                action.id
              ));
            }),
            finalize(() => ctx.patchState({ inProgress: false }))
          );
      }
    }
  }

  @Action([ChecklistTemplateEntryManagerAction.ShiftLeft, ChecklistTemplateEntryManagerAction.ShiftRight])
  public shift(
    ctx: StateContext<ChecklistTemplateEntryManagerStateModel>,
    action: ChecklistTemplateEntryManagerAction.ShiftLeft | ChecklistTemplateEntryManagerAction.ShiftRight
  ): Observable<void> {
    const state: ChecklistTemplateEntryManagerStateModel = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Checklist template entry request is in progress!'));
    }

    if (!state.active) {
      return throwError(() => new Error('Checklist template entry manager is not active!'));
    }

    if (state.mode !== ChecklistTemplateEntryManagerMode.EDIT) {
      return throwError(() => new Error('Shift can only be executed in edit mode!'));
    }

    const checklistTemplateId = this.store.selectSnapshot(ChecklistTemplateDetailXsState.id);

    if (checklistTemplateId == null) {
      return throwError(() =>
        new Error('Cannot shift checklist template entry! State returned null id for checklist template!')
      );
    }

    const shiftLeft: boolean = action instanceof ChecklistTemplateEntryManagerAction.ShiftLeft;

    ctx.patchState({
      inProgress: true
    });

    return from(this.checklistTemplateEntryRequestService.shiftChecklistTemplateEntryLevelToLeft(
      checklistTemplateId,
      state.id,
      shiftLeft
    ))
      .pipe(
        switchMap(() => {
          return this.store.dispatch(new ChecklistTemplateDetailAction.ChecklistTemplateEntryShifted(
            state.id
          ));
        }),
        tap(() => ctx.setState(getDefaults)),
        finalize(() => ctx.patchState({ inProgress: false }))
      );
  }

  @Action([
    ChecklistTemplateEntryManagerAction.ResetState,
    ResetStateDefaults
  ])
  public resetState(
    ctx: StateContext<ChecklistTemplateEntryManagerStateModel>
  ): void {
    ctx.setState({ ...getDefaults() });
  }

  @Action(ChecklistTemplateEntryManagerAction.AddFiles)
  public addFile(
    ctx: StateContext<ChecklistTemplateEntryManagerStateModel>,
    action: ChecklistTemplateEntryManagerAction.AddFiles
  ): Observable<void> {
    const state: ChecklistTemplateEntryManagerStateModel = ctx.getState();

    if (!state.active || !state.mode) {
      return throwError(() =>
        new Error('Cannot save checklist template entry when state is not active or edit mode is not defined!')
      );
    }

    if (!action.files) {
      return throwError(() => new Error('File cannot be null!'));
    }

    if (!state.checklistTemplateEntry) {
      return throwError(() => new Error(
        'Checklist template entry cannot be null! Entry state must be updated before saving the entry!'
      ));
    }

    if (state.inProgress) {
      return throwError(() => new Error('Checklist template entry request is in progress!'));
    }

    ctx.patchState({
      inProgress: true
    });

    return from(action.files).pipe(
      concatMap((file: File) => {
        return from(FileUploaderUtils.uploadFile(file, this.app)).pipe(
          catchError((err: FileUploadError) => {
            if (err === FileUploadError.FILE_TOO_BIG) {
              this.app.on.inProgressAsyncWorkFinished$.next(MessageDesignBUtils.fileUploadFileSizeTooBig());
            }
            return EMPTY;
          }),
          filter((file: DocumentModel | null): file is DocumentModel => !!file),
          tap((uploadedFile: DocumentModel) => {
            const currentState = ctx.getState();
            let optionId: string;
            if (currentState.checklistTemplateEntry?.options) {
              optionId = currentState.checklistTemplateEntry.options[action.optionIndex].id;
            } else {
              optionId = null;
            }

            let existingFiles: DocumentGroupModel[];
            if (optionId !== null) {
              const existingOption = currentState.updatedChecklistTemplateEntry.options.find(o => o.id === optionId);
              existingFiles = existingOption?.files ?? [];
            } else {
              const existingOption = currentState.updatedChecklistTemplateEntry.options[action.optionIndex];
              existingFiles = existingOption?.files ?? [];
            }

            const newFiles = ProcessUtils.initNewFilesByEditProcess(
              [uploadedFile],
              DocumentGroupReferenceType.CHECKLIST_TEMPLATE_ENTRY_OPTION,
              optionId
            );

            let updatedOptions: ChecklistTemplateEntryOption[];
            if (optionId !== null) {
              updatedOptions = state.updatedChecklistTemplateEntry.options.map(o => {
                if (o.id === optionId) {
                  return {
                    ...o,
                    files: [...existingFiles, ...newFiles]
                  };
                }
                return o;
              });
            } else {
              updatedOptions = currentState.updatedChecklistTemplateEntry.options.map((o, i) => {
                if (i === action.optionIndex) {
                  return {
                    ...o,
                    files: [...existingFiles, ...newFiles]
                  };
                }
                return o;
              });
            }

            ctx.patchState({
              updatedChecklistTemplateEntry: {
                ...state.updatedChecklistTemplateEntry,
                options: updatedOptions
              },
              optionFiles: true
            });
          })
        );
      }),
      finalize(() => {
        ctx.patchState({ inProgress: false });
      }),
      map(() => void 0)
    );
  }

  @Action(ChecklistTemplateEntryManagerAction.RemoveFile)
  public removeFile(
    ctx: StateContext<ChecklistTemplateEntryManagerStateModel>,
    action: ChecklistTemplateEntryManagerAction.RemoveFile
  ): Observable<void> {
    const state: ChecklistTemplateEntryManagerStateModel = ctx.getState();

    if (!action.file?.documentGroupId) {
      ctx.patchState({
        inProgress: true
      });
      const originalOption = state.updatedChecklistTemplateEntry.options[action.optionIndex];
      const files = [...originalOption.files];
      files.splice(action.fileIndex, 1);
      const updatedOption: ChecklistTemplateEntryOption = {
        ...originalOption,
        files: files
      };
      ctx.patchState({
        updatedChecklistTemplateEntry: {
          ...state.updatedChecklistTemplateEntry,
          options: state.updatedChecklistTemplateEntry.options.map((o, index) => {
            if (index === action.optionIndex) {
              return { ...updatedOption };
            }
            return o;
          })
        }
      });
      ctx.patchState({
        inProgress: false
      });
      return;
    }

    if (state.id === null) {
      return throwError(() => new Error('State is not initialized!'));
    }

    if (state.inProgress === null) {
      return throwError(() => new Error('Request is in progress!'));
    }

    ctx.patchState({
      inProgress: true
    });

    const originalOption = state.updatedChecklistTemplateEntry.options[action.optionIndex];
    const filesToDelete = [...originalOption?.filesToDelete == null ? [] : originalOption.filesToDelete];

    filesToDelete.push(action.file.documentGroupId);

    const updatedOption: ChecklistTemplateEntryOption = {
      ...originalOption,
      filesToDelete: filesToDelete
    };


    ctx.patchState({
      updatedChecklistTemplateEntry: {
        ...state.updatedChecklistTemplateEntry,
        options: state.updatedChecklistTemplateEntry.options.map(o => {
          if (o.id === updatedOption.id) {
            return { ...updatedOption };
          }
          return o;
        })
      }
    });
    ctx.patchState({
      inProgress: false,
      filesToDelete: true
    });
  }

  @Action(ChecklistTemplateEntryManagerAction.AddImage)
  public addImage(
    ctx: StateContext<ChecklistTemplateEntryManagerStateModel>,
    action: ChecklistTemplateEntryManagerAction.AddImage
  ) {
    const state: ChecklistTemplateEntryManagerStateModel = ctx.getState();

    if (!state.active || !state.mode) {
      return throwError(() =>
        new Error('Cannot save checklist template entry when state is not active or edit mode is not defined!')
      );
    }

    if (!state.checklistTemplateEntry) {
      return throwError(() => new Error(
        'Checklist template entry cannot be null! Entry state must be updated before saving the entry!'
      ));
    }

    if (state.inProgress) {
      return throwError(() => new Error('Checklist template entry request is in progress!'));
    }

    ctx.patchState({
      inProgress: true
    });

    const removeImages = this.store.selectSnapshot(ChecklistTemplateEntryManagerXsState.removeImages);


    if (action.optionIndex != null) {
      //SET for option
      const optionImages = state.optionImages;
      optionImages.set(action.optionIndex, action.image);

      removeImages.options.delete(action.optionIndex);
      ctx.patchState({
        optionImages: optionImages,
        removeImages: { entry: removeImages.entry, options: removeImages.options },
        inProgress: false
      });
    } else {
      //SET for entry
      ctx.patchState({
        entryImage: action.image,
        removeImages: { entry: false, options: removeImages.options },
        inProgress: false
      });
    }
  }

  @Action(ChecklistTemplateEntryManagerAction.SetRemoveImages)
  public setImageRemoval(
    ctx: StateContext<ChecklistTemplateEntryManagerStateModel>,
    action: ChecklistTemplateEntryManagerAction.SetRemoveImages
  ) {
    const state: ChecklistTemplateEntryManagerStateModel = ctx.getState();

    const removeOptions = this.store.selectSnapshot(ChecklistTemplateEntryManagerXsState.removeImages).options;
    let removeEntryImage: boolean = false;

    if (action.optionIndex !== null) {
      removeOptions.set(action.optionIndex, true);
    } else {
      removeEntryImage = true;
    }

    ctx.patchState({
      removeImages: { entry: removeEntryImage, options: removeOptions }
    });
  }

  @Action(ChecklistTemplateEntryManagerAction.DeleteImage)
  public deleteImage(
    ctx: StateContext<ChecklistTemplateEntryManagerStateModel>,
    action: ChecklistTemplateEntryManagerAction.DeleteImage
  ) {
    const state: ChecklistTemplateEntryManagerStateModel = ctx.getState();

    if (!state.active || !state.mode) {
      return throwError(() =>
        new Error('Cannot save checklist template entry when state is not active or edit mode is not defined!')
      );
    }

    if (!state.checklistTemplateEntry) {
      return throwError(() => new Error(
        'Checklist template entry cannot be null! Entry state must be updated before saving the entry!'
      ));
    }

    const checklistTemplateId = this.store.selectSnapshot(ChecklistTemplateDetailXsState.id);
    const checklistTemplateEntry: ChecklistTemplateEntry = this.store.selectSnapshot(ChecklistTemplateEntryManagerXsState.checklistTemplateEntry);
    const checklistTemplateEntryId = checklistTemplateEntry.id;


    if (action.optionIndex != null) {
      //DELETE for option
      const checklistTemplateOptionId = checklistTemplateEntry.options[action.optionIndex].id;

      return from(this.checklistTemplateEntryRequestService.deleteOptionImage(
        checklistTemplateId,
        checklistTemplateEntryId,
        checklistTemplateOptionId)
      ).pipe(finalize(() => {
        const optionImages = state.optionImages;
        optionImages.set(action.optionIndex, null);

        ctx.patchState({
          optionImages: optionImages
        });
      }));
    } else {
      //DELETE for entry
      return from(this.checklistTemplateEntryRequestService.deleteEntryImage(
        checklistTemplateId,
        checklistTemplateEntryId)
      ).pipe(finalize(() => {
        const optionImages = state.optionImages;
        optionImages.set(action.optionIndex, null);

        ctx.patchState({
          entryImage: null
        });
      }));

    }
  }

  @Action(ChecklistTemplateEntryManagerAction.PasteDuplicateEntries)
  public pasteDuplicateEntries(
    ctx: StateContext<ChecklistTemplateEntryManagerStateModel>,
    action: ChecklistTemplateEntryManagerAction.PasteDuplicateEntries
  ): Observable<void> {
    const state: ChecklistTemplateEntryManagerStateModel = ctx.getState();
    const targetChecklistTemplateVersionId
      = this.store.selectSnapshot(ChecklistTemplateDetailXsState.currentVersion)?.id;

    if (state.inProgress) {
      return throwError(() => new Error('Checklist template entry request is in progress!'));
    }

    ctx.patchState({
      inProgress: true
    });

    return from(this.checklistTemplateEntryRequestService.pasteDuplicateEntries(
      targetChecklistTemplateVersionId,
      action.entryId,
      action.duplicateBlock,
      action.insertBeforeEntryId,
      action.insertAfterEntryId
    ))
      .pipe(
        switchMap(() => {
          return this.store.dispatch(new ChecklistTemplateDetailAction.ChecklistTemplateEntryDuplicated());
        }),
        tap(() => ctx.setState(getDefaults)),
        finalize(() => ctx.patchState({ inProgress: false }))
      );
  }


  @Action(ChecklistTemplateEntryManagerAction.PasteDuplicateAllEntries)
  public pasteDuplicateAllEntries(
    ctx: StateContext<ChecklistTemplateEntryManagerStateModel>,
    action: ChecklistTemplateEntryManagerAction.PasteDuplicateAllEntries
  ) {
    const state: ChecklistTemplateEntryManagerStateModel = ctx.getState();
    const targetChecklistTemplateVersionId
      = this.store.selectSnapshot(ChecklistTemplateDetailXsState.currentVersion)?.id;

    if (state.inProgress) {
      return throwError(() => new Error('Checklist template entry request is in progress!'));
    }

    ctx.patchState({
      inProgress: true
    });

    return from(this.checklistTemplateEntryRequestService.pasteDuplicateAllEntries(
      targetChecklistTemplateVersionId,
      action.checklistTemplateVersionId,
      action.insertBeforeEntryId,
      action.insertAfterEntryId
    ))
      .pipe(
        switchMap(() => {
          return this.store.dispatch(new ChecklistTemplateDetailAction.ChecklistTemplateEntryDuplicated());
        }),
        tap(() => ctx.setState(getDefaults)),
        finalize(() => ctx.patchState({ inProgress: false }))
      );
  }

  //endregion
}
